import React, { useEffect, useState } from "react";
import { Phone, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DialerKeypad from "@/components/dialer/DialerKeypad";
import DncCheckButton from "@/components/compliance/DncCheckButton";

interface MainDialerContentProps {
  keypadInput: string;
  onKeypadInputChange: (value: string) => void;
  onCall: (phoneNumber: string) => void;
  onHeightChange?: (height: number) => void;
}

export default function MainDialerContent({
  keypadInput,
  onKeypadInputChange,
  onCall,
  onHeightChange,
}: MainDialerContentProps) {
  const [activeTab, setActiveTab] = useState<string>("quick-dial");
  const [contactInput, setContactInput] = useState<string>("");

  useEffect(() => {
    const height = activeTab === "keypad" ? 750 : 400;
    onHeightChange?.(height);
  }, [activeTab, onHeightChange]);

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="quick-dial">Quick Dial</TabsTrigger>
          <TabsTrigger value="keypad">Keypad</TabsTrigger>
        </TabsList>

        <TabsContent value="quick-dial" className="space-y-4 mt-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Contact name, phone number or user"
              className="pl-10 dark:text-white"
              value={contactInput}
              onChange={(e) => setContactInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && contactInput) {
                  onCall(contactInput);
                }
              }}
            />
          </div>

          <div className="flex gap-2">
            <Button
              className="h-12 flex-1 bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white"
              onClick={() => onCall(contactInput)}
              disabled={!contactInput}
            >
              <Phone className="h-4 w-4 mr-2" />
              Call
            </Button>
            <DncCheckButton phoneNumbers={[contactInput]} className="h-12" />
          </div>
        </TabsContent>

        <TabsContent value="keypad" className="mt-4 space-y-3">
          <DialerKeypad
            value={keypadInput}
            onChange={onKeypadInputChange}
            onCall={() => onCall(keypadInput)}
            disabled={!keypadInput}
          />
          <div className="flex justify-center">
            <DncCheckButton phoneNumbers={[keypadInput]} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
